import { navigatorClient } from "@/api";
import type { ApiResponse } from "@/types";

// F02 Case Study (backend /case-studies)
export interface CaseIndustry {
  code: string | null; // 고른 노드 (1 · 2단계)
  root_code: string | null; // 1단계 — 필터 · 아이콘 기준
  label: string;
}

export interface CaseCard {
  id: string;
  title: string;
  industry: CaseIndustry;
  cover_image_url: string | null;
  card_metric_value: string | null;
  card_metric_label: string | null;
  agent: { name: string; logo_url: string | null } | null;
}

export interface CaseList {
  items: CaseCard[];
  total: number;
  industries: { code: string; label: string; count: number }[];
}

export type CaseBlock =
  | { type: "paragraph"; text: string }
  | { type: "image"; url: string; caption?: string | null }
  | { type: "callout"; title: string; text: string }
  | { type: "quote"; text: string; source?: string | null };

export interface CaseSections {
  overview?: { rows: { label: string; value: string }[] } | null;
  challenge?: { blocks: CaseBlock[] };
  solution?: { blocks: CaseBlock[] };
  effect?: { blocks: CaseBlock[] };
  process?: { steps: { name: string; period: string; description: string }[] } | null;
}

export interface CaseMetric {
  label: string;
  value: string;
  comparison?: string | null;
  basis?: string | null;
}

export interface CaseAgent {
  id: string | null;
  name: string;
  is_verified: boolean;
  supplier_name: string | null;
  category: string | null;
  logo_url: string | null;
  available: boolean;
}

export interface CaseDetail {
  id: string;
  title: string;
  summary: string | null;
  industry: CaseIndustry;
  customer_display: "real" | "anonymous";
  customer_label: string | null;
  tasks: string[];
  cover_image_url: string | null;
  metrics: CaseMetric[];
  sections: CaseSections;
  agent: CaseAgent | null;
  others: CaseCard[];
}

export async function listCaseStudies(industries: string[], signal?: AbortSignal): Promise<CaseList> {
  const { data } = await navigatorClient.get<ApiResponse<CaseList>>("/case-studies", {
    params: industries.length ? { industry: industries.join(",") } : undefined,
    signal,
  });
  return data.data!;
}

// 공급자 등록 신청 (목록 [Case Study 등록 신청] 팝업)
export interface CaseIndustryNode {
  id: string;
  code: string;
  label: string;
  children: { id: string; code: string; label: string }[];
}

export interface CaseApplicationOptions {
  agents: { id: string; name: string }[];
  industries: CaseIndustryNode[];
}

export interface CaseApplicationRequest {
  agent_id: string | null;
  agent_label_other: string | null;
  industry_node_id: string | null;
  industry_label_other: string | null;
  customer_display: "real" | "anonymous";
  customer_label: string;
  description: string;
  performance: string | null;
}

export async function getCaseApplicationOptions(signal?: AbortSignal): Promise<CaseApplicationOptions> {
  const { data } = await navigatorClient.get<ApiResponse<CaseApplicationOptions>>("/case-studies/application-options", { signal });
  return data.data!;
}

export async function applyCaseStudy(payload: CaseApplicationRequest): Promise<{ id: string }> {
  const { data } = await navigatorClient.post<ApiResponse<{ id: string }>>("/case-studies/applications", payload);
  return data.data!;
}

export async function getCaseStudy(id: string, source?: string, signal?: AbortSignal): Promise<CaseDetail> {
  const { data } = await navigatorClient.get<ApiResponse<CaseDetail>>(`/case-studies/${id}`, { params: { source }, signal });
  return data.data!;
}
